'use client';

import React, { useEffect, useRef } from 'react';
import { useLang } from './LanguageContext';

const featureIcons = [
  // Expert Guides
  <svg key="1" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <circle cx="12" cy="8" r="4"/><path d="M6 20v-2a6 6 0 0 1 12 0v2"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>,
  // Best Prices
  <svg key="2" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
  </svg>,
  // 24/7 Support
  <svg key="3" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>,
  // Custom Tours
  <svg key="4" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/>
  </svg>,
  // Visa
  <svg key="5" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <rect x="3" y="5" width="18" height="14" rx="2"/>
    <path d="M3 10h18M7 15h2M12 15h5"/>
  </svg>,
  // Safety
  <svg key="6" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    <path d="M9 12l2 2 4-4"/>
  </svg>,
];

export default function WhyUsSection() {
  const { t } = useLang();
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.08 }
    );
    sectionRef?.current?.querySelectorAll('.reveal')?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  const features = [1, 2, 3, 4, 5, 6]?.map((n) => ({
    icon: featureIcons?.[n - 1],
    title: t(`why_${n}_title`),
    desc: t(`why_${n}_desc`),
  }));

  const titleLines = t('why_title')?.split('\n');

  return (
    <section id="why" ref={sectionRef} className="py-24 md:py-32 relative">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'rgba(21,35,54,0.3)' }}
      />
      <div className="max-w-8xl mx-auto px-6 md:px-10 relative z-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
          <div>
            <div className="section-label mb-4 reveal">{t('why_label')}</div>
            <h2
              className="font-serif font-bold text-cream reveal reveal-delay-1"
              style={{
                fontFamily: 'Fraunces, serif',
                fontSize: 'clamp(2rem, 5vw, 3.8rem)',
                lineHeight: 1.1,
              }}
            >
              {titleLines?.map((line, i) => (
                <span key={i} className="block">
                  {i === 0 ? line : <span className="text-shimmer">{line}</span>}
                </span>
              ))}
            </h2>
          </div>
        </div>

        {/* Feature grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features?.map((feat, i) => (
            <div
              key={i}
              className={`feature-card reveal`}
              style={{ transitionDelay: `${i * 0.08}s` }}
            >
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6 text-gold"
                style={{ background: 'rgba(201,168,76,0.1)' }}
              >
                {feat?.icon}
              </div>
              <h3
                className="font-serif font-bold text-cream text-xl mb-3"
                style={{ fontFamily: 'Fraunces, serif' }}
              >
                {feat?.title}
              </h3>
              <p className="text-muted text-sm leading-relaxed">{feat?.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}